import { useState } from "react";
import { trpc } from "../../lib/trpc.js";
import { dolar, dataHora, diaCurto, inteiro, real } from "../../lib/formato.js";
import { Lista, Pagina } from "../../components/Pagina.js";
import { Barras } from "../../components/grafico.js";
import { Botao, Girando, Selo, Vazio, cx } from "../../components/ui.js";

/**
 * Para onde o dinheiro da IA está indo.
 *
 * O número que abre a tela é o custo por foto entregue — tudo que a IA gastou,
 * dividido pelas pessoas atendidas. Ele inclui as tentativas que falharam, e por
 * isso é maior do que o custo de uma geração feliz: é o que se paga de verdade
 * por foto na mão de alguém.
 */

type Nivel = "instituicao" | "vivencia" | "operador" | "modelo" | "chamadas";

/**
 * Cem por página.
 *
 * O maior evento até hoje somou 55 chamadas, então na prática tudo cabe numa
 * página só — e o total do rodapé, que é do recorte inteiro, coincide com a
 * soma do que está à vista.
 */
const CHAMADAS_POR_PAGINA = 100;

const ATALHOS = [
  { rotulo: "7 dias", dias: 7 },
  { rotulo: "30 dias", dias: 30 },
  { rotulo: "90 dias", dias: 90 },
] as const;

function isoDeDiasAtras(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
}

export function CustosPage() {
  const [dias, setDias] = useState<number | null>(null);
  /**
   * O nome viaja junto do id.
   *
   * Procurar o nome na lista não funciona: ao entrar num nível, o filtro muda a
   * chave da consulta e a lista anterior deixa de existir — a trilha ficava
   * dizendo "Local/Evento" em vez do nome da casa.
   */
  const [instituicao, setInstituicao] = useState<{ id: string; nome: string } | null>(null);
  const [vivencia, setVivencia] = useState<{ id: string; nome: string } | null>(null);
  const instituicaoId = instituicao?.id ?? null;
  const vivenciaId = vivencia?.id ?? null;
  const [nivel, setNivel] = useState<Nivel>("instituicao");
  const [moeda, setMoeda] = useState<"USD" | "BRL">("BRL");
  const [pagina, setPagina] = useState(0);

  /**
   * Trocar de recorte ou de nível invalida a página em que se estava.
   *
   * Entrar numa vivência com a página 3 aberta mostraria "nada aqui" sem
   * explicar por quê.
   */
  const irPara = (mudanca: () => void) => {
    mudanca();
    setPagina(0);
  };

  const recorte = {
    dateFrom: dias ? isoDeDiasAtras(dias) : undefined,
    institutionId: instituicaoId ?? undefined,
    vivenciaId: vivenciaId ?? undefined,
  };

  const panorama = trpc.aiCosts.panorama.useQuery(recorte);
  const cambio = trpc.aiCosts.exchangeRate.useQuery();
  const porDia = trpc.aiCosts.porDia.useQuery(recorte);

  const instituicoes = trpc.aiCosts.porInstituicao.useQuery(recorte, {
    enabled: nivel === "instituicao",
  });
  const vivenciasQ = trpc.aiCosts.porVivencia.useQuery(recorte, { enabled: nivel === "vivencia" });
  const operadores = trpc.aiCosts.porOperador.useQuery(recorte, { enabled: nivel === "operador" });
  const modelos = trpc.aiCosts.porModelo.useQuery(recorte, { enabled: nivel === "modelo" });
  const chamadas = trpc.aiCosts.chamadas.useQuery(
    { ...recorte, limit: CHAMADAS_POR_PAGINA, offset: pagina * CHAMADAS_POR_PAGINA },
    { enabled: nivel === "chamadas" },
  );

  const p = panorama.data;
  const taxa = Number(cambio.data?.sellRate ?? 0);
  const semCusto = p?.cobertura.semCusto ?? 0;

  /**
   * Todo valor da tela passa por aqui.
   *
   * O sistema paga em dólar e a casa presta contas em real; mostrar só um dos
   * dois obriga alguém a fazer a conta de cabeça. Sem cotação disponível, cai
   * para dólar em vez de exibir número errado.
   */
  const emReais = moeda === "BRL" && taxa > 0;
  const valor = (usd: string | number | null | undefined) =>
    emReais ? real(Number(usd ?? 0) * taxa) : dolar(usd);

  /** A trilha do drill-down: clicar numa casa entra nela, e o caminho volta. */
  const trilha = [
    ...(instituicao
      ? [
          {
            rotulo: instituicao.nome,
            aoVoltar: () =>
              irPara(() => {
                setInstituicao(null);
                setVivencia(null);
                setNivel("instituicao");
              }),
          },
        ]
      : []),
    ...(vivencia
      ? [
          {
            rotulo: vivencia.nome,
            aoVoltar: () =>
              irPara(() => {
                setVivencia(null);
                setNivel("vivencia");
              }),
          },
        ]
      : []),
  ];

  return (
    <Pagina
      titulo="Custos de IA"
      descricao="Quanto custa cada foto entregue, e para onde o gasto está indo."
    >
      {/* Empilha no celular e só então vira duas pontas: com quatro atalhos de
          período mais a moeda, uma linha só espremeria tudo num aparelho de
          390px. */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1.5">
          {ATALHOS.map((a) => (
            <Botao
              key={a.dias}
              tom={dias === a.dias ? "brasa" : "quieto"}
              onClick={() => irPara(() => setDias(dias === a.dias ? null : a.dias))}
            >
              {a.rotulo}
            </Botao>
          ))}
          <Botao
            tom={dias === null ? "brasa" : "quieto"}
            onClick={() => irPara(() => setDias(null))}
          >
            Tudo
          </Botao>
        </div>

        {/* Encostado à direita também no celular: a moeda é a segunda decisão
            da tela, e à esquerda ela competiria com os atalhos de período. */}
        <div className="flex gap-1.5 self-end sm:self-auto">
          <Botao
            tom={moeda === "BRL" ? "brasa" : "quieto"}
            disabled={taxa <= 0}
            onClick={() => setMoeda("BRL")}
          >
            R$
          </Botao>
          <Botao tom={moeda === "USD" ? "brasa" : "quieto"} onClick={() => setMoeda("USD")}>
            US$
          </Botao>
        </div>
      </div>

      {trilha.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[0.8125rem] text-texto-suave">
          {trilha.map((t) => (
            <Botao key={t.rotulo} tom="fantasma" formato="nu" onClick={t.aoVoltar}>
              ‹ {t.rotulo}
            </Botao>
          ))}
        </div>
      )}

      {panorama.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Indicador
              rotulo="Custo por foto"
              valor={p?.custoPorFotoUsd ? valor(p.custoPorFotoUsd) : "—"}
              apoio={`${inteiro(p?.fotosEntregues)} entregue${p?.fotosEntregues === 1 ? "" : "s"}`}
              destaque
            />
            <Indicador
              rotulo="Custo total"
              valor={valor(p?.custoUsd)}
              apoio={
                emReais
                  ? dolar(p?.custoUsd)
                  : taxa > 0
                    ? real(Number(p?.custoUsd ?? 0) * taxa)
                    : "sem cotação"
              }
              destaque
            />
            <Indicador
              rotulo="Desperdício"
              valor={valor(p?.custoDesperdicadoUsd)}
              apoio={
                Number(p?.perdaEstimadaUsd ?? 0) > 0
                  ? `${inteiro(p?.falhas)} perdida${p?.falhas === 1 ? "" : "s"}, custo estimado`
                  : `${inteiro(p?.falhas)} tentativa${p?.falhas === 1 ? "" : "s"} perdida${p?.falhas === 1 ? "" : "s"}`
              }
            />
            <Indicador rotulo="Chamadas" valor={inteiro(p?.chamadas)} apoio="à IA no período" />
          </div>

          {emReais && cambio.data && (
            <p className="mt-3 text-[0.75rem] text-texto-fraco">
              Convertido a R$ {Number(cambio.data.sellRate).toFixed(4).replace(".", ",")} por dólar,
              cotação de {cambio.data.date.split("-").reverse().join("/")}.
            </p>
          )}

          {semCusto > 0 && (
            <p className="mt-3 text-[0.8125rem] text-texto-fraco">
              {inteiro(semCusto)} chamada{semCusto === 1 ? "" : "s"} sem custo informado pelo
              provedor. Os valores acima são parciais.
            </p>
          )}

          {(porDia.data?.length ?? 0) > 1 && (
            <section className="mt-6">
              <h2 className="mb-2 text-[0.8125rem] font-medium text-texto-suave">Por dia</h2>
              <Barras
                dados={(porDia.data ?? []).map((d) => ({
                  rotulo: diaCurto(d.dia),
                  valor: Number(d.custoUsd),
                }))}
                formatar={valor}
              />
            </section>
          )}

          <section className="mt-6">
            <div className="mb-3 flex flex-wrap gap-1.5">
              {(
                [
                  ["instituicao", "Locais/Eventos"],
                  ["vivencia", "Vivências"],
                  ["operador", "Operadores"],
                  ["modelo", "Modelos"],
                  ["chamadas", "Chamadas"],
                ] as const
              ).map(([valor, rotulo]) => (
                <Botao
                  key={valor}
                  tom={nivel === valor ? "brasa" : "quieto"}
                  onClick={() => irPara(() => setNivel(valor))}
                >
                  {rotulo}
                </Botao>
              ))}
            </div>

            {nivel === "instituicao" && (
              <Recorte
                formatar={valor}
                itens={(instituicoes.data ?? []).map((i) => ({
                  chave: i.id ?? "sem",
                  nome: i.nome ?? "Sem local",
                  chamadas: i.chamadas,
                  custoUsd: i.custoUsd,
                }))}
                aoAbrir={(chave, nome) => {
                  if (chave === "sem") return;
                  irPara(() => {
                    setInstituicao({ id: chave, nome });
                    setNivel("vivencia");
                  });
                }}
              />
            )}

            {nivel === "vivencia" && (
              <Recorte
                formatar={valor}
                itens={(vivenciasQ.data ?? []).map((v) => ({
                  chave: v.id ?? "sem",
                  nome: v.nome ?? "Sem vivência",
                  apoio: v.instituicao ?? undefined,
                  chamadas: v.chamadas,
                  custoUsd: v.custoUsd,
                }))}
                aoAbrir={(chave, nome) => {
                  if (chave === "sem") return;
                  // Abre as chamadas daquele evento. "Operadores" continua na
                  // barra de cima, já filtrado pela vivência em que se está.
                  irPara(() => {
                    setVivencia({ id: chave, nome });
                    setNivel("chamadas");
                  });
                }}
              />
            )}

            {nivel === "operador" && (
              <Recorte
                formatar={valor}
                itens={(operadores.data ?? []).map((o) => ({
                  chave: String(o.id ?? "sem"),
                  // O histórico não tem operador: dizer isso é melhor que "—".
                  nome: o.nome ?? "Antes do registro de operador",
                  chamadas: o.chamadas,
                  custoUsd: o.custoUsd,
                }))}
              />
            )}

            {nivel === "modelo" && (
              <Recorte
                formatar={valor}
                itens={(modelos.data ?? []).map((m) => ({
                  chave: `${m.modelo}-${m.servico}`,
                  nome: m.modelo ?? "sem modelo",
                  apoio: m.servico === "image_suggest" ? "sugestão de prompt" : "geração",
                  chamadas: m.chamadas,
                  custoUsd: m.custoUsd,
                }))}
              />
            )}

            {nivel === "chamadas" &&
              (chamadas.isLoading ? (
                <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
              ) : (
                <>
                  <TabelaDeChamadas
                    itens={chamadas.data?.itens ?? []}
                    total={chamadas.data?.total ?? 0}
                    totalUsd={chamadas.data?.custoUsd ?? "0"}
                    formatar={valor}
                  />

                  {(chamadas.data?.total ?? 0) > CHAMADAS_POR_PAGINA && (
                    <div className="mt-6 flex items-center justify-between">
                      <Botao
                        tom="quieto"
                        disabled={pagina === 0}
                        onClick={() => setPagina((n) => n - 1)}
                      >
                        Anterior
                      </Botao>
                      <span className="text-[0.8125rem] text-texto-fraco">
                        {pagina + 1} de{" "}
                        {Math.ceil((chamadas.data?.total ?? 0) / CHAMADAS_POR_PAGINA)}
                      </span>
                      <Botao
                        tom="quieto"
                        disabled={(pagina + 1) * CHAMADAS_POR_PAGINA >= (chamadas.data?.total ?? 0)}
                        onClick={() => setPagina((n) => n + 1)}
                      >
                        Próxima
                      </Botao>
                    </div>
                  )}
                </>
              ))}
          </section>
        </>
      )}
    </Pagina>
  );
}

function Indicador({
  rotulo,
  valor,
  apoio,
  destaque,
}: {
  rotulo: string;
  valor: string;
  apoio?: string;
  destaque?: boolean;
}) {
  return (
    <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
      <p className="text-[0.75rem] uppercase tracking-[0.1em] text-texto-fraco">{rotulo}</p>
      <p
        className={cx("num mt-1 font-display text-[1.5rem] leading-none", destaque && "text-brasa")}
      >
        {valor}
      </p>
      {apoio && <p className="mt-1 text-[0.75rem] text-texto-fraco">{apoio}</p>}
    </div>
  );
}

interface LinhaChamada {
  id: number;
  quando: Date | string;
  custoUsd: string | null;
  sucesso: boolean;
  vivencia: string | null;
  participante: string | null;
  sobrenome: string | null;
  telefone: string | null;
  nomeDigitado: string | null;
}

/**
 * Quem foi atendido nesta chamada.
 *
 * O cadastro vem primeiro. Sem ele, o nome digitado no atendimento — marcado
 * como digitado, porque não passou por cadastro e pode ser só um primeiro nome.
 * Sem nem isso, a chamada é anônima de verdade: falha antes de haver pessoa, ou
 * geração já apagada.
 */
function quemFoi(i: LinhaChamada): { nome: string | null; digitado: boolean } {
  if (i.participante) {
    return { nome: [i.participante, i.sobrenome].filter(Boolean).join(" "), digitado: false };
  }
  return { nome: i.nomeDigitado, digitado: i.nomeDigitado !== null };
}

/**
 * As chamadas uma a uma, com o total ao pé.
 *
 * O total é do recorte inteiro, não da página: é o que faz este número bater com
 * o que a vivência mostrava no nível anterior.
 */
function TabelaDeChamadas({
  itens,
  total,
  totalUsd,
  formatar,
}: {
  itens: LinhaChamada[];
  total: number;
  totalUsd: string;
  formatar: (v: string | number | null | undefined) => string;
}) {
  if (itens.length === 0) {
    return <Vazio titulo="Nada no período" descricao="Escolha outro intervalo ou outro recorte." />;
  }

  // Custo ausente é falha que não chegou a ser cobrada — travessão, e não zero,
  // que se somaria visualmente ao total sem ter entrado nele.
  const custo = (v: string | null) => (v === null ? "—" : formatar(v));

  const nomeDe = (i: LinhaChamada) => {
    const { nome, digitado } = quemFoi(i);
    if (!nome) return <span className="text-texto-fraco">—</span>;
    return (
      <>
        {nome}
        {digitado && <span className="ml-2 text-[0.75rem] text-texto-fraco">digitado</span>}
      </>
    );
  };

  const rotuloTotal = `Total — ${inteiro(total)} chamada${total === 1 ? "" : "s"}`;

  return (
    <Lista
      itens={itens}
      chave={(i) => i.id}
      colunas={["Vivência", "Participante", "Telefone", "Data", "Custo"]}
      linha={(i) => [
        <span key="v" className="text-texto-suave">
          {i.vivencia ?? "—"}
        </span>,
        <span key="p">
          {nomeDe(i)}
          {!i.sucesso && (
            <span className="ml-2">
              <Selo tom="descartar">falhou</Selo>
            </span>
          )}
        </span>,
        <span key="t" className="num font-mono text-[0.875rem] text-texto-suave">
          {i.telefone ?? "—"}
        </span>,
        <span key="d" className="num text-texto-suave">
          {dataHora(i.quando)}
        </span>,
        <span key="c" className="num">
          {custo(i.custoUsd)}
        </span>,
      ]}
      cartao={(i) => (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[0.9375rem] text-texto">
              {nomeDe(i)}
              {!i.sucesso && (
                <span className="ml-2">
                  <Selo tom="descartar">falhou</Selo>
                </span>
              )}
            </p>
            <p className="num mt-0.5 font-mono text-[0.8125rem] text-texto-fraco">
              {i.telefone ?? "sem telefone"}
            </p>
            <p className="mt-1 text-[0.75rem] text-texto-fraco">
              {i.vivencia ?? "sem vivência"} · {dataHora(i.quando)}
            </p>
          </div>
          <span className="num shrink-0 text-[0.9375rem] text-texto">{custo(i.custoUsd)}</span>
        </div>
      )}
      rodape={{
        celulas: [
          rotuloTotal,
          "",
          "",
          "",
          <span key="c" className="num">
            {formatar(totalUsd)}
          </span>,
        ],
        cartao: (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[0.9375rem]">{rotuloTotal}</span>
            <span className="num text-[0.9375rem]">{formatar(totalUsd)}</span>
          </div>
        ),
      }}
    />
  );
}

interface LinhaRecorte {
  chave: string;
  nome: string;
  apoio?: string;
  chamadas: number;
  custoUsd: string;
}

/**
 * Um nível do drill-down.
 *
 * A barra proporcional é o mesmo desenho das três que já existem no sistema —
 * trilho e preenchimento — e serve para comparar de relance sem ler número.
 */
function Recorte({
  itens,
  formatar,
  aoAbrir,
}: {
  itens: LinhaRecorte[];
  /** A moeda escolhida no topo vale para a tela inteira. */
  formatar: (v: string | number | null | undefined) => string;
  aoAbrir?: (chave: string, nome: string) => void;
}) {
  if (itens.length === 0) {
    return <Vazio titulo="Nada no período" descricao="Escolha outro intervalo ou outro recorte." />;
  }

  const maior = Math.max(...itens.map((i) => Number(i.custoUsd)), 0.000001);

  return (
    <ul className="flex flex-col gap-2">
      {itens.map((i) => {
        const valor = Number(i.custoUsd);
        const conteudo = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 truncate text-[0.9375rem] text-texto">
                {i.nome}
                {i.apoio && (
                  <span className="ml-2 text-[0.8125rem] text-texto-fraco">{i.apoio}</span>
                )}
              </span>
              <span className="num shrink-0 text-[0.9375rem] text-texto">{formatar(valor)}</span>
            </div>
            <div className="mt-2 h-1 rounded-full bg-borda">
              <div
                className="h-full rounded-full bg-projecao"
                style={{ width: `${Math.max((valor / maior) * 100, 1)}%` }}
              />
            </div>
            <p className="mt-1.5 text-[0.75rem] text-texto-fraco">
              {inteiro(i.chamadas)} chamada{i.chamadas === 1 ? "" : "s"}
            </p>
          </>
        );

        return (
          <li key={i.chave}>
            {aoAbrir ? (
              <button
                onClick={() => aoAbrir(i.chave, i.nome)}
                className="w-full rounded-peca border border-borda bg-superficie px-4 py-3 text-left transition-colors hover:border-borda-forte"
              >
                {conteudo}
              </button>
            ) : (
              <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
                {conteudo}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
